import { z } from "zod";

import { baseEntityFields, customFieldsField } from "./base.js";

/**
 * Credential : famille "attestation à durée de validité".
 *
 * Attestation officielle ou interne à durée de validité, portée par une
 * personne (worker), un équipement (asset) ou l'organisation, dont l'expiration
 * doit être anticipée pour continuer à exercer ou exploiter (habilitation,
 * certification, licence, permis, assurance...). Construite sur le modèle
 * "famille + type" de la bible (patron : `asset`).
 *
 * Le socle ne connaît pas la nature métier concrète ("habilitation
 * électrique", "certificat ISO", "permis cariste") : il connaît la famille
 * générique `credential`. Le vocabulaire métier est porté par le champ `type`
 * (chaîne libre déclarée par l'app), qui est le `kind` de la fiche renommé
 * selon la convention du socle.
 *
 * Porteur (holder) polymorphe, en références optionnelles (aucun import de
 * schéma) : `workerId` si l'attestation est portée par une personne, `assetId`
 * si elle est portée par un équipement ; les deux absents, elle est portée par
 * l'organisation elle-même (déjà cadrée par `orgId`). Les autres liens sont
 * aussi de simples ids optionnels : `proofDocumentId` (pièce justificative,
 * référence `document`), `taskId` (tâche de renouvellement, référence `task`),
 * `inspectionId` (contrôle associé, référence `inspection`).
 *
 * Accès : v0 reste org-wide (même doctrine que asset). Rien de sensible signalé
 * sur cette famille.
 *
 * Convention de patron (cf. asset) :
 *   - `xCoreFields`  : les champs MÉTIER seuls (sans les champs standard).
 *   - `XSchema`      : famille complète = champs standard + champs métier.
 *   - `XCreateInputSchema` : ce que l'appelant fournit à la création, VALIDÉ
 *     AVANT toute écriture Firestore.
 */

/** Champs métier de la famille credential (hors champs standard du socle). */
export const credentialCoreFields = {
  orgId: z.string(),
  /** Discriminant de sous-type déclaré par l'app (ex. "habilitation", "certification"). C'est le `kind` de la fiche. */
  type: z.string().min(1),
  /** Porteur = personne (référence `worker`) ; absent si porté par l'équipement ou l'org. Optionnel. */
  workerId: z.string().min(1).optional(),
  /** Porteur = équipement (référence `asset`) ; absent si porté par la personne ou l'org. Optionnel. */
  assetId: z.string().min(1).optional(),
  /** Autorité ou organisme émetteur, chaîne libre (ex. "AFNOR", "interne"). Optionnel. */
  issuer: z.string().min(1).optional(),
  /** Date de délivrance. Optionnel. */
  issuedAt: z.date().optional(),
  /** Date d'expiration, à anticiper (cœur métier de la famille). Optionnel. */
  expiresAt: z.date().optional(),
  /** Pièce justificative (référence `document`). Optionnel. */
  proofDocumentId: z.string().min(1).optional(),
  /** Délai d'alerte avant expiration, en jours. Optionnel. */
  alertBeforeDays: z.number().int().optional(),
  /** Tâche de renouvellement liée (référence `task`). Optionnel. */
  taskId: z.string().min(1).optional(),
  /** Contrôle lié (référence `inspection`). Optionnel. */
  inspectionId: z.string().min(1).optional(),
} as const;

/** Schéma complet de la famille credential (champs standard + champs métier). */
export const CredentialSchema = z.object({
  ...baseEntityFields,
  ...credentialCoreFields,
});

/** Type d'une credential. */
export type Credential = z.infer<typeof CredentialSchema>;

/**
 * Schéma des données fournies par l'appelant à la création.
 * Les champs standard (id, dates, auteurs, archivedAt, deletedAt, schemaVersion) sont
 * remplis par le SDK et ne sont donc PAS attendus ici.
 */
export const CredentialCreateInputSchema = z.object({
  ...credentialCoreFields,
  ...customFieldsField,
});

/** Type de l'entrée de création d'une credential. */
export type CredentialCreateInput = z.infer<typeof CredentialCreateInputSchema>;

/**
 * Schema du patch de mise a jour (update partiel).
 * `orgId` et `type` sont immuables (Security Rules) ; les champs standard
 * sont geres par le SDK : aucun des deux n'est accepte ici.
 */
export const CredentialUpdateInputSchema = z
  .object({ ...credentialCoreFields, ...customFieldsField })
  .omit({ orgId: true, type: true })
  .partial();

/** Type du patch de mise a jour d'une credential. */
export type CredentialUpdateInput = z.infer<typeof CredentialUpdateInputSchema>;
